/**
 * ChainLightningSkillEffect — Lightning tree arc-path evolution: replaces Arc
 * with a professional-grade chain bolt.
 *
 * Four targets at base (primary + 3 chain hops), 90% damage retention per hop,
 * longer initial range, same medium cooldown. Arc was a party trick. Chain
 * Lightning is what a Conductor does to a hallway full of people who made poor
 * life choices.
 *
 * Evolution inheritance: queries the 'arc' resolver scope for Forked and
 * Conduit upgrades (option b — explicit scope query, not automatic fallback).
 * rollSkillSuccess uses 'chain_lightning' for independent mastery tracking.
 *
 * Upgrade keys consumed via UpgradeEffectResolver (scoped to 'arc'):
 * - `chainTargets` (additive) — +1 from Forked (more chain hops)
 * - `chainDamageRetention` (multiplicative) — 0.85 from Forked (per-hop rate)
 * - `stunnedNodeChainRangeMultiplier` (additive) — x2 from Conduit
 * - `chainThroughStunned` (boolean) — from Conduit (relay condition)
 *
 * Cross-bucket read (scoped to 'bolt'):
 * - `rodBonusChain` (additive) — +1 from Ionized (extra hop through rod target)
 * - `chainRangeMultiplierInField` (additive) — 2x from Conducted (first hop in field)
 *
 * Tree passive interaction:
 * - Gathering Storm: when total targets hit >= chainThreshold, applies
 *   gathering_storm_buff to player (bonusDamage + bonusArea for next skill)
 *
 * Fizzle: cooldown consumed, no damage, no chain.
 * Out-of-range: no cooldown consumed.
 * Chain origin is the primary target's position, not the player's.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see lightning-spec.md § Chain Lightning
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ChainLightningChainConfig {
  readonly originX: number;
  readonly originY: number;
  readonly maxTargets: number;
  readonly maxRangeTiles: number;
  readonly damageRetention: number;
  readonly mode: 'single';
  readonly bonusRangeForStatus?: {
    readonly statusId: string;
    readonly rangeMultiplier: number;
  };
  readonly relayCondition?: {
    readonly statusId: string;
  };
  readonly rodBonusChain?: number;
  readonly rodStatusId?: string;
  /** Range multiplier for the first hop only (Conducted conductive field). */
  readonly firstHopRangeMultiplier?: number;
}

export interface ChainLightningChainResult {
  readonly targetId: string;
  readonly damageMultiplier: number;
  readonly activeStatuses: string[];
}

export interface ChainLightningDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  /** Consume all charge for the given ID. Returns amount consumed. */
  readonly consumeCharge: (chargeId: string) => number;
  /**
   * Find chain targets from a starting position. Returns ordered targets
   * with accumulated damage multipliers and their active statuses (needed
   * for rod-status consumption after chaining).
   */
  readonly findChainTargets: (config: ChainLightningChainConfig) => ChainLightningChainResult[];
  /** Returns entity ID of nearest enemy within range, or null if none. */
  readonly getTarget: (rangeTiles: number) => string | null;
  /** Returns position of target entity (used as chain origin). */
  readonly getTargetPosition: (targetId: string) => { x: number; y: number };
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /** Deal direct (non-DoT) hit damage to a single target. */
  readonly dealDamage: (targetId: string, damage: number) => void;
  /** Query tree-scope passive effects (for Gathering Storm thresholds). */
  readonly getTreePassiveEffect: (treeId: string, key: string, aggregation: 'product' | 'sum') => number;
  /** Apply a player-side status buff (e.g., gathering_storm_buff). */
  readonly applyPlayerStatus: (statusId: string, config: { durationMs: number; intensity: number }) => void;
  /** Remove a status from an enemy target (e.g., consume lightning_rod). */
  readonly consumeStatus: (targetId: string, statusId: string) => void;
  /** Check if player is inside a zone of the given type (e.g., conductive_field). */
  readonly isInsideZoneType: (type: string) => boolean;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ChainLightningConfig {
  /** Cooldown in ms after activation or fizzle. Default: 800 (medium tier). */
  readonly cooldownMs: number;
  /** Base bolt damage before static charge bonus. */
  readonly baseDamage: number;
  /** Base initial bolt range in tiles. Default: 4 (longer than Arc's 3). */
  readonly baseRangeTiles: number;
  /** Base chain hop range in tiles. */
  readonly baseChainRangeTiles: number;
  /** Base number of chain hops (excluding primary). Default: 3 (4 total). */
  readonly baseChainTargets: number;
  /** Base damage retention per hop. Default: 0.90. */
  readonly baseDamageRetention: number;
  /** Ratio of consumed charge converted to bonus damage. Default: 0.1. */
  readonly chargeBonusDamageRatio: number;
  /** Charge ID to consume from ChargeSystem. Default: 'static_charge'. */
  readonly chargeId: string;
}

const DEFAULT_CONFIG: ChainLightningConfig = {
  cooldownMs: 800,                  // medium cooldown tier
  baseDamage: 12,                   // slightly higher than Arc's 10
  baseRangeTiles: 4,                // longer initial range than Arc's 3
  baseChainRangeTiles: 2,           // same chain hop distance as Arc
  baseChainTargets: 3,              // 3 chain hops (4 total with primary)
  baseDamageRetention: 0.90,        // 90% per hop (Arc has none at base)
  chargeBonusDamageRatio: 0.1,      // matches Static passive default
  chargeId: 'static_charge',
};

/** Arc bucket — resolver scope for inherited Forked/Conduit upgrades. */
const RESOLVER_SCOPE = 'arc';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ChainLightningSkillEffect implements SkillEffect {
  readonly skillId = 'chain_lightning';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: ChainLightningDeps;
  private readonly config: ChainLightningConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _outOfRangeAttempts = 0;
  private _totalChainHits = 0;
  private _lastTotalTargetsHit = 0;
  private _lastResolvedChainRangeTiles = 0;

  constructor(deps: ChainLightningDeps, config: Partial<ChainLightningConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get outOfRangeAttempts(): number { return this._outOfRangeAttempts; }
  get totalChainHits(): number { return this._totalChainHits; }
  /** Total targets hit in the most recent activation (primary + chain). */
  get lastTotalTargetsHit(): number { return this._lastTotalTargetsHit; }
  /** Effective first-hop chain range from the most recent activation. */
  get resolvedChainRangeTiles(): number { return this._lastResolvedChainRangeTiles; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Range check — no target = no cooldown consumed
    const targetId = this.deps.getTarget(this.config.baseRangeTiles);
    if (targetId === null) {
      this._outOfRangeAttempts++;
      return false;
    }

    // Fizzle check — cooldown IS consumed on fizzle, uses own skillId
    if (!this.deps.rollSkillSuccess('chain_lightning')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break hide on successful activation
    this.deps.breakHide();

    // Calculate primary damage: base + static charge bonus
    const chargeConsumed = this.deps.consumeCharge(this.config.chargeId);
    const chargeBonusDamage = chargeConsumed * this.config.chargeBonusDamageRatio;
    const primaryDamage = this.config.baseDamage + chargeBonusDamage;

    // Deal primary hit damage
    this.deps.dealDamage(targetId, primaryDamage);

    // Chain targeting from primary hit position (not player position)
    const targetPos = this.deps.getTargetPosition(targetId);

    // Resolve chain parameters from arc-scoped upgrades
    const chainTargets = this.config.baseChainTargets
      + this.deps.resolver.getAdditiveEffect(RESOLVER_SCOPE, 'chainTargets');
    const damageRetention = this.config.baseDamageRetention
      * this.deps.resolver.getMultiplicativeEffect(RESOLVER_SCOPE, 'chainDamageRetention');

    // Conduit: stunned enemies get bonus chain range
    const stunnedRangeMultiplier = this.deps.resolver.getAdditiveEffect(
      RESOLVER_SCOPE, 'stunnedNodeChainRangeMultiplier',
    );
    const bonusRangeForStatus = stunnedRangeMultiplier > 0
      ? { statusId: 'stun', rangeMultiplier: stunnedRangeMultiplier }
      : undefined;

    // Conduit: stunned enemies act as relay nodes
    const chainThroughStunned = this.deps.resolver.hasEffect(
      RESOLVER_SCOPE, 'chainThroughStunned',
    );
    const relayCondition = chainThroughStunned
      ? { statusId: 'stun' }
      : undefined;

    // Ionized: rod-marked targets grant extra chain hops
    const rodBonusChain = this.deps.resolver.getAdditiveEffect('bolt', 'rodBonusChain');

    // Conducted: conductive field doubles first hop range when player stands in it
    const chainRangeMultiplierInField = this.deps.resolver.getAdditiveEffect(
      'bolt', 'chainRangeMultiplierInField',
    );
    const firstHopRangeMultiplier = (chainRangeMultiplierInField > 0
      && this.deps.isInsideZoneType('conductive_field'))
      ? chainRangeMultiplierInField
      : undefined;

    this._lastResolvedChainRangeTiles = this.config.baseChainRangeTiles
      * (firstHopRangeMultiplier ?? 1);

    const chainResults = this.deps.findChainTargets({
      originX: targetPos.x,
      originY: targetPos.y,
      maxTargets: chainTargets,
      maxRangeTiles: this.config.baseChainRangeTiles,
      damageRetention,
      mode: 'single',
      bonusRangeForStatus,
      relayCondition,
      rodBonusChain: rodBonusChain > 0 ? rodBonusChain : undefined,
      rodStatusId: rodBonusChain > 0 ? 'lightning_rod' : undefined,
      firstHopRangeMultiplier,
    });

    // Apply chain hits with accumulated retention multipliers
    for (const result of chainResults) {
      this.deps.dealDamage(result.targetId, primaryDamage * result.damageMultiplier);
      this._totalChainHits++;

      // Consume lightning_rod on targets that had it (Ionized interaction)
      if (rodBonusChain > 0 && result.activeStatuses.includes('lightning_rod')) {
        this.deps.consumeStatus(result.targetId, 'lightning_rod');
      }
    }

    // Track total targets hit (primary + chain)
    this._lastTotalTargetsHit = 1 + chainResults.length;

    // Gathering Storm: when total targets >= threshold, apply buff to player
    const chainThreshold = this.deps.getTreePassiveEffect(
      'lightning', 'chainThreshold', 'sum',
    );
    if (chainThreshold > 0 && this._lastTotalTargetsHit >= chainThreshold) {
      this.deps.applyPlayerStatus('gathering_storm_buff', {
        durationMs: 10000,
        intensity: 1,
      });
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
